import appConfig from 'configs/app'
import { trackEvent } from 'helpers/analytics'
import { sendTaggedMessage } from 'helpers/log'
import { getPageUrlWithoutQuery } from 'helpers/url'
import type {
  CaptchaSolveResult,
  CaptchaTelemetry,
  CaptchaTelemetryEvent,
} from 'helpers/captcha/contracts'

const UNKNOWN_VALUE = 'unknown'
const TELEMETRY_PLATFORM = 'web'
const UNDER_ONE_SECOND_MS = 1000
const THREE_SECONDS_MS = 3000
const EIGHT_SECONDS_MS = 8000
const DURATION_UNDER_ONE_SECOND = '<1s'
const DURATION_ONE_TO_THREE_SECONDS = '1-3s'
const DURATION_THREE_TO_EIGHT_SECONDS = '3-8s'
const DURATION_OVER_EIGHT_SECONDS = '>8s'

export function captchaErrorCodeOf(
  result: CaptchaSolveResult,
): string {
  if (result.outcome === 'failed') return result.code
  if (result.outcome === 'unsupported_mode') return result.mode
  return UNKNOWN_VALUE
}

function durationBucket(elapsedMs: number): string {
  if (elapsedMs < UNDER_ONE_SECOND_MS) {
    return DURATION_UNDER_ONE_SECOND
  }
  if (elapsedMs < THREE_SECONDS_MS) {
    return DURATION_ONE_TO_THREE_SECONDS
  }
  if (elapsedMs < EIGHT_SECONDS_MS) {
    return DURATION_THREE_TO_EIGHT_SECONDS
  }
  return DURATION_OVER_EIGHT_SECONDS
}

function eventParams(event: CaptchaTelemetryEvent) {
  const params: Record<string, string> = {
    captcha_operation: event.operation,
    platform: TELEMETRY_PLATFORM,
    // Mixpanel otherwise attaches $current_url with the query string.
    $current_url: getPageUrlWithoutQuery(),
    $referrer: '',
    $initial_referrer: '',
    environment: appConfig.environment,
    captcha_provider: event.challenge?.provider ?? UNKNOWN_VALUE,
    captcha_mode: event.challenge?.mode ?? UNKNOWN_VALUE,
    app_version: appConfig.publicVersion,
  }

  if (event.result) {
    params.captcha_result =
      event.result.outcome === 'failed'
        ? 'client_error'
        : event.result.outcome
  }
  if (event.errorCode) params.captcha_error_code = event.errorCode
  if (event.elapsedMs !== undefined) {
    params.captcha_duration = durationBucket(event.elapsedMs)
  }

  return params
}

/**
 * Mixpanel events and Sentry messages carry only the allowlisted fields
 * above, never the phone number or the Turnstile cData derived from it.
 */
export const captchaTelemetry: CaptchaTelemetry = {
  track(event) {
    try {
      trackEvent(event.name, eventParams(event))
    } catch {
      // Telemetry must not alter challenge behavior.
    }
  },
  reportUnexpected(issue) {
    try {
      sendTaggedMessage(
        `Captcha: unexpected ${issue.phase} failure (${issue.errorCode})`,
        {
          operation: issue.operation,
          phase: issue.phase,
          error_code: issue.errorCode,
          provider: issue.challenge?.provider ?? UNKNOWN_VALUE,
          mode: issue.challenge?.mode ?? UNKNOWN_VALUE,
        },
      )
    } catch {
      // Telemetry must not alter challenge behavior.
    }
  },
}
